/**
 * WHICH LANGUAGE, AND WHOSE.
 *
 * Pure decisions about locale tags: no vue-i18n, no Vue, no network. It is here
 * rather than inside the seed so the cascade can be tested as arithmetic — given
 * these four inputs, this locale — instead of by booting an app.
 *
 * ── THE TWO RESOLUTIONS, AND WHY CONFUSING THEM IS THE BUG ────────────────
 *
 * There are two different languages in this product and they answer different
 * questions.
 *
 *   THE PANEL is the language of the PERSON: their own preference, then the
 *   workspace's, then the browser's, then English. `resolvePanelLocale` below is
 *   that cascade and the only thing that decides what a colleague reads.
 *
 *   EVERY PUBLIC SURFACE and everything that leaves for a CUSTOMER — the public
 *   chat, a form, a document, a site page, an agent's answer, a template, an
 *   invoice line — is the language of the TENANT, and never the language of the
 *   browser looking at it. That one is `workspace.language`, carried by
 *   `DISPLAY_CONTEXT`, and it is deliberately NOT computed here.
 *
 * A Brazilian operator who switches their own panel to English must not thereby
 * make their customers receive English. If a future caller ever reaches for
 * `resolvePanelLocale` to decide what a customer reads, the answer is no.
 */

/**
 * The languages this panel actually speaks.
 *
 * English FIRST because English is the origin: keys and source sentences are
 * written in it and a missing translation falls back to it. Greek joined as the
 * third, and the shape took it without a refactor — one more block per
 * dictionary and one more member here — which was the claim this line used to
 * make about a hypothetical third and now records as done.
 *
 * THIS LIST IS WHAT MAKES A LANGUAGE REAL. Fifty-one dictionaries carried a
 * translated `el` block for a while and the panel still could not be read in
 * Greek, because nothing outside this array knows the language exists: the
 * cascade will not resolve to it, the picker will not offer it, and the API
 * refuses to store it. Translating is the long half of the work; this line is
 * the half that turns it on.
 *
 * Mirrors `PANEL_LANGUAGES` in the API (`identity/api/dto/auth.dto.ts`), which
 * refuses to store anything outside it.
 */
export const SUPPORTED_LOCALES = ["en", "pt-BR", "el", "es", "fr", "it", "de"] as const;

export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

/**
 * The end of every cascade.
 *
 * English and not pt-BR, even though most of today's users read Portuguese: a
 * fallback is what answers when nothing is known, and the only sentence
 * guaranteed to exist for every key is the source one.
 */
export const DEFAULT_LOCALE: AppLocale = "en";

/** How each locale names itself. Never translated — a language list in a language you cannot read is useless. */
export const LOCALE_NAMES: Record<AppLocale, string> = {
	en: "English",
	"pt-BR": "Português (Brasil)",
	el: "Ελληνικά",
	es: "Español",
	fr: "Français",
	it: "Italiano",
	de: "Deutsch",
};

/**
 * A tag from anywhere — a column, a browser, a stored hint — as a locale we ship.
 *
 * Matched by the PRIMARY SUBTAG, so `pt`, `pt-PT` and `pt_BR` all land on
 * Portuguese. That is what a browser sending `pt-PT` means and refusing it would
 * be answering "we do not have your exact dialect" with English, which is the
 * worse of the two wrongs. Case and separator are normalised because
 * `navigator.languages`, a database column and a `localStorage` value have all
 * been seen writing this differently.
 *
 * `null` and not a default: the caller is a cascade, and a step that cannot
 * answer has to say so rather than end it.
 */
export function normalizeLocale(tag: string | null | undefined): AppLocale | null {
	if (!tag) return null;
	const primary = tag.trim().toLowerCase().replace(/_/g, "-").split("-")[0];
	if (!primary) return null;
	return SUPPORTED_LOCALES.find((known) => known.toLowerCase().split("-")[0] === primary) ?? null;
}

/**
 * What the four sources of a panel language are, in the order they win.
 *
 * Every field is optional and each may be an unshippable tag: this is fed raw
 * server and browser values on purpose, because a cascade that demands clean
 * input needs a second cascade in front of it to clean it.
 */
export interface PanelLocaleSources {
	/** What THIS PERSON chose, from `app_user.language`. Null means they never chose. */
	readonly user?: string | null;
	/** What the WORKSPACE speaks, from `tenant.language`. The sensible answer for a colleague who never chose. */
	readonly workspace?: string | null;
	/** What the browser asks for, most-preferred first — `navigator.languages`. */
	readonly browser?: readonly string[] | null;
}

/**
 * The panel cascade: person → workspace → browser → English.
 *
 * The workspace outranks the browser deliberately. A team in São Paulo issued
 * laptops in English still works in Portuguese, and the workspace is the only
 * one of the three that somebody actually decided; the browser is a machine
 * setting that follows whoever installed the operating system.
 */
export function resolvePanelLocale(sources: PanelLocaleSources): AppLocale {
	const fromBrowser = (sources.browser ?? []).map(normalizeLocale).find((l) => l !== null);
	return (
		normalizeLocale(sources.user) ??
		normalizeLocale(sources.workspace) ??
		fromBrowser ??
		DEFAULT_LOCALE
	);
}

/**
 * Where the boot HINT is remembered.
 *
 * A hint and not the preference: the preference lives on the profile, server
 * side, because a person who signs in on a second machine is the same person.
 * This exists only so the first paint after a reload is not in the wrong
 * language for the second it takes the profile and the workspace to arrive — and
 * it is overwritten by the real cascade the moment they do, which is what keeps
 * a workspace changing its language from being outranked forever by a stale
 * browser value.
 */
const STORAGE_KEY = "benvox.panel-locale";

export function readStoredLocale(): AppLocale | null {
	try {
		return normalizeLocale(localStorage.getItem(STORAGE_KEY));
	} catch {
		// Private mode, a blocked origin, SSR: a missing hint is not a failure.
		return null;
	}
}

export function writeStoredLocale(locale: AppLocale): void {
	try {
		localStorage.setItem(STORAGE_KEY, locale);
	} catch {
		// Same: the profile is the record, this was only the shortcut.
	}
}

/**
 * The locale the app opens in, before a single request has answered.
 *
 * The stored hint, then the browser, then English — the same cascade minus the
 * two facts nobody knows yet.
 */
export function initialLocale(): AppLocale {
	const browser =
		typeof navigator === "undefined" ? [] : (navigator.languages ?? [navigator.language]);
	return readStoredLocale() ?? resolvePanelLocale({ browser });
}
